import React from "react";

/**
 * Destructive confirmation dialog.
 *
 * Brand values are the app's own: #E53E14 primary, #FFF5F3 tint, #363538 ink.
 * The confirm button is the brand red because the brand red IS the warning
 * colour here — introducing a second, different red for "danger" would read as a
 * third colour rather than as emphasis.
 *
 * NOTE: this project loads only `@tailwind utilities` (see src/index.css) —
 * there is NO preflight, so a bare <button> keeps Chrome's default outset border
 * and grey face. That is why `border-0 appearance-none` is spelled out, and why
 * the cancel button has to ask for its border back explicitly.
 */
const DeletePopUp = ({ data, yes, cancel }) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-dialog-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-[12vh]"
      style={{ backgroundColor: "rgba(27, 27, 41, 0.55)", backdropFilter: "blur(2px)" }}
    >
      <div
        className="w-full max-w-md bg-white"
        style={{
          borderRadius: "16px",
          boxShadow: "0 20px 48px rgba(16,24,40,0.18), 0 4px 12px rgba(16,24,40,0.08)",
        }}
      >
        <div className="p-6">
          {/* A tinted icon rather than a bare heading: it says "destructive" before
              anyone reads the sentence. */}
          <div
            className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: "#FFF5F3" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.6}
              stroke="#E53E14"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </div>

          <h3
            id="delete-dialog-title"
            className="text-lg font-semibold normal-case"
            style={{ color: "#363538" }}
          >
            Delete this item?
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-500">{data}</p>
          <p className="mt-2 text-sm text-gray-400">This cannot be undone.</p>
        </div>

        {/* Cancel first and visually quieter: the destructive action should never
            be the one the eye lands on, or the one a reflex click hits. */}
        <div
          className="flex justify-end gap-3 border-0 border-t border-solid border-gray-100 px-6 py-4"
          style={{ background: "#FAFAFA", borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}
        >
          <button
            type="button"
            onClick={cancel}
            className="cursor-pointer border border-solid border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 appearance-none transition-colors hover:bg-gray-50"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={yes}
            className="cursor-pointer border-0 px-5 py-2.5 text-sm font-semibold text-white appearance-none transition-opacity hover:opacity-90"
            style={{
              background: "#E53E14",
              borderRadius: "10px",
              boxShadow: "0 1px 2px rgba(229,62,20,0.35)",
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeletePopUp;
